import _ from 'lodash';
import * as members from '../../privates/privateMembers';
import {emptyStringIfNil, nullIfNil} from '../../privates/utils';
import {validate} from '../../privates/validations/validation';

/**
 * @mixin TextInputMixin
 * @summary Provides functionality for elements that accept text as input.
 * @description
 *  `TextInputMixin` is a mixin that provides basic properties and event handlers for elements on the page that can accept text as input.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  let maxLen = $w("#TextInputMixin").maxLength;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `TextInputMixin`. 
 * 
 *  ```javascript
 *  let maxLen = $w("#myElement").maxLength;
 *  ```
 *  
 * @memberof $w
 */
class TextInputMixin {
    /**
     * @member placeholder
     * @label property
     * @syntax
     * get placeholder(): string
     * set placeholder(value: string): void
     * @summary Sets or gets the element's placeholder text.
     * @description
     *  Placeholder text is typically used to provide a
     *  hint to the user of what to enter into a input element that accepts
     *  text. When the user begins to enter text, the placeholder text disappears.
     * @snippet [TextInputElement-placeholder_get.es6=Get an element's placeholder text]
     * @snippet [TextInputElement-placeholder_set.es6=Set an element's placeholder text]
     * @type {external:String}
     * @memberof $w.TextInputMixin
     * @attr {external:String}
     */
    get placeholder() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compProps = RMI.getProps(compId);
        const compData = RMI.getData(compId);

        return _.get(compData, 'placeholder') || _.get(compProps, 'placeholder');
    }

  /**
   * @member placeholder
   * @memberof $w.TextInputMixin
   * @param {external:String} newPlaceholder
   */
    set placeholder(newPlaceholder) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'placeholder',
            dataToValidate: {placeholder: newPlaceholder},
            compName: this.id
        });
        if (newPlaceholder && !isValid) {
            return;
        }
        newPlaceholder = emptyStringIfNil(newPlaceholder);
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        RMI.setData(compId, {
            placeholder: newPlaceholder
        });
        RMI.setProps(compId, {
            placeholder: newPlaceholder
        });
    }

    /**
     * @member maxLength
     * @label property
     * @syntax
     * get maxLength(): Number
     * set maxLength(value: Number): void
     * @summary Sets or gets the maximum number of characters that can be entered.
     * @description
     *  Setting the `maxLength` property of an input element to a
     *  positive number restricts user input, by manual entry or pasting, to
     *  text that does not exceed the `maxLength`. However, it does not limit
     *  your ability to change the element's value to one that exceeds
     *  `maxLength` programmatically.
     *
     *  To remove the maximum length restriction that has been set on the input
     *  element, set `maxLength` to null or undefined.
     *
     *  Getting the `maxLength` property returns the element's
     *  current `maxLength` setting.
     *
     *  The number of characters is calculated by [Unicode code points](https://en.wikipedia.org/wiki/Code_point).
     * @snippet [TextInputMixin-maxlength_get.es6=Get an element's maximum length]
     * @snippet [TextInputMixin-maxlength_set.es6=Set an element's maximum length]
     * @snippet [TextInputMixin-maxlength_reset.es6=Remove an element's maximum length restriction]
     * @type {external:Number}
     * @memberof $w.TextInputMixin
     */
    get maxLength() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        return compData && compData.maxLength;
    }

  /**
   * @member maxLength
   * @memberof $w.TextInputMixin
   * @param {external:Number} len
   */
    set maxLength(len) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'maxlength',
            dataToValidate: {maxlength: len},
            compName: this.id
        });
        if (!_.isNil(len) && !isValid) {
            return;
        }
        len = nullIfNil(len);
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        RMI.setData(compId, {
            maxLength: len
        });
    }


    /**
     * @function onKeyPress
     * @syntax
     * function onKeyPress(handler: KeyboardEventHandler): Element
     * callback KeyboardEventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the cursor is inside the
     *  input element and a key is pressed.
     * @description
     *  A text input receives a `keyPress` event when a user presses a key on
     *  the keyboard while the cursor is inside the input element. A `keyPress`
     *  event is fired for printable and non-printable characters.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * > **Notes:**
     * > + Some browsers do not issue a key press event for certain keys,
     *  such as arrow keys or the shift key.
     * >
     * >
     * > + When you retrieve the `value` from the `target` property of a [`KeyboardEvent`]($w.KeyboardEvent.html),
     *  you get the value of the target element before the key was pressed. To get the new value,
     *  use [`onInput`](#onInput).
     * >
     * >
     * > + **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.KeyboardEventHandler} eventHandler The name of the function or
     *  the function expression to run when a key is pressed.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [TextInputElement-onKeyPress.es6=Get the key that was pressed]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     * @memberof $w.TextInputMixin
     * @eventtype keyPress
     * @eventhandler
     */
    onKeyPress(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onKeyPress',
            dataToValidate: {onKeyPress: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);
        const contextId = members._contextId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().KEY_PRESS, controllerId, cb);
        return this;
    }

    /**
     * @function onInput
     * @summary Adds an event handler that runs when the input element receives
     * input.
     * @description
     *  A text input receives an `input` event when a user changes its input value.
     * 
     *  You can retrieve the input element's new value by getting the `value` from the
     *  `target` property of the [`Event`]($w.Event.html) passed to the handler you set
     *  using this function.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.EventHandler} eventHandler The name of the function or
     *  the function expression to run when the input element receives
     *  input.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [TextInputElement-onInput.es6=Get the new input value]
     * @memberof $w.TextInputMixin
     * @eventtype onInput
     * @eventhandler
     */
    onInput(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onInput',
            dataToValidate: {onInput: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);
        const contextId = members._contextId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ON_INPUT, controllerId, cb);
        return this;
    }
}


export default TextInputMixin;
